// ShortAd.jsx
import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import styled, { css, keyframes } from 'styled-components';
import { useTranslation } from 'react-i18next';
import { sanitize } from 'isomorphic-dompurify';
import { addBreadcrumb } from '@sentry/browser';

import LinkSubstring from '../container/LinkSubstring.js';
import ShareButton from '../container/ShareButton.js';
import Tooltip from '../component/Tooltip.jsx';
import {
  CTA_HEIGHT,
  CTA_MOBILE_HEIGHT,
  METADATA_DETAIL_GAP,
  METADATA_SIZE,
  TRANSITION_MILLI_SECONDS,
} from '../component/ShortMessage.jsx';

import { ShareType } from '../hook/useShortShareLink.js';

import { breakpoint, color } from '../style/variables.js';
import ResetButtonStyle from '../style/ResetButtonStyle.js';
import media from '../style/media.js';
import TextEllipsis from '../style/TextEllipsis.js';
import { shortsPlayerZIndex } from '../style/variables.js';

import { ButtonId } from '../resource/trackingConstants.js';
import { TranslationNamespace } from '../resource/translationNamespace.js';
import {
  TYPE_PHOTO,
  TYPE_VIDEO,
} from '../resource/popupNotificationConstants.js';
import { PLAY_STATE } from '../resource/playerUtils.js';
import { getIsValidLink } from '../resource/getInternalLink.js';
import { Severity } from '../resource/sentry.js';

import MuteIconSource from '../../img/ic_mute_32px.svg';
import AudioIconSource from '../../img/ic_audio_32px.svg';
import PlayPaddingIconSource from '../../img/ic_play_padding.svg';
import { NULL_FUNCTION } from '../resource/defaults.js';

export const ShortAd = ({
  adId,
  adLink,
  adData = null,
  isActive = null,
  isMuted = true,
  currentLanguage = '',
  fetchOembedData = NULL_FUNCTION,
  setPlayerData = NULL_FUNCTION,
}) => {
  const { t } = useTranslation();
  const videoWrapperRef = useRef();
  const shortMessageRef = useRef();
  const playButtonTransitionTimeout = useRef(null);
  const [playState, setPlayState] = useState(PLAY_STATE.Play);
  const [shouldStartPlayAnimation, setShouldStartPlayAnimation] =
    useState(false);
  const [shouldShowAllCaption, setShouldShowAllCaption] = useState(false);
  let ctaText = '';
  if (getIsValidLink({ link: adLink })) {
    const urlParams = new URL(adLink);
    const params = new URLSearchParams(urlParams.search);
    ctaText = params.get('cta_text') || '';
  }

  const trackingPayload = {
    messageId: adId,
    ...(adLink
      ? {
          'cta.url': adLink,
        }
      : {}),
  };

  useEffect(() => {
    if (!adLink) return;
    addBreadcrumb({
      category: 'ShortAd',
      level: Severity.Debug,
      message: 'dispatch fetchOembedData()',
      data: {
        adLink,
      },
    });

    fetchOembedData({
      url: adLink,
      language: currentLanguage,
    });
  }, [adLink, fetchOembedData, currentLanguage]);

  useEffect(() => {
    return () => {
      clearTimeout(playButtonTransitionTimeout.current);
    };
  }, []);

  const getVideoElement = useCallback(() => {
    if (!videoWrapperRef.current || adData?.type !== TYPE_VIDEO) {
      return null;
    }
    const videoElement = videoWrapperRef.current.querySelector(`#${adId}`);
    if (!videoElement) {
      return null;
    }

    return videoElement;
  }, [adData?.type, adId]);

  useEffect(() => {
    const videoElement = getVideoElement();
    if (!videoElement) return;
    videoElement.muted = isMuted;
  }, [getVideoElement, isMuted]);

  const togglePlay = () => {
    const videoElement = getVideoElement();
    if (!videoElement) return;

    switch (playState) {
      case PLAY_STATE.Pause: {
        setShouldStartPlayAnimation(true);
        playButtonTransitionTimeout.current = setTimeout(() => {
          setShouldStartPlayAnimation(false);
        }, TRANSITION_MILLI_SECONDS);
        setPlayState(PLAY_STATE.Play);
        videoElement.play();
        break;
      }
      case PLAY_STATE.Play: {
        setPlayState(PLAY_STATE.Pause);
        videoElement.pause();
        break;
      }
      default: {
        return;
      }
    }
  };

  const renderControlButton = () => {
    const shouldShowControlButton =
      getVideoElement() &&
      (playState === PLAY_STATE.Pause || shouldStartPlayAnimation);

    if (!shouldShowControlButton) return null;

    return (
      <PlayerControlButton
        type="button"
        data-element_id={
          PLAY_STATE.Play === playState
            ? ButtonId.Detail.PlayerControlsPause
            : ButtonId.Detail.PlayerControlsPlay
        }
        data-tracking_payload={trackingPayload}
        onClick={togglePlay}
        isPlaying={shouldStartPlayAnimation}
      >
        <img src={PlayPaddingIconSource} width="60" height="60" alt="" />
      </PlayerControlButton>
    );
  };
  const renderPhoto = () => <Photo src={adData?.url} alt="" />;
  const renderVideo = () => {
    const html = sanitize(adData?.html)?.replace(
      '<video',
      `<video id="${adId}" autoplay playsinline loop`
    );
    return (
      <VideoWrapper
        ref={videoWrapperRef}
        dangerouslySetInnerHTML={{
          __html: html,
        }}
      />
    );
  };

  const renderMediaContent = () => {
    return (
      <>
        {renderControlButton()}
        {adData?.type === TYPE_VIDEO && renderVideo()}
        {adData?.type === TYPE_PHOTO && renderPhoto()}
      </>
    );
  };

  const sponserName = adData?.author_name || '';
  const caption = adData?.title || '';
  return (
    <StyledShortAd
      shouldShowAllCaption={shouldShowAllCaption}
      ref={shortMessageRef}
      data-element_id={
        PLAY_STATE.Play === playState
          ? ButtonId.Detail.PlayerControlsPause
          : ButtonId.Detail.PlayerControlsPlay
      }
      data-tracking_payload={trackingPayload}
      onClick={event => {
        if (event.target === shortMessageRef.current) {
          return togglePlay();
        }
      }}
    >
      {isActive && renderMediaContent()}
      <ActionWrapper>
        <MessageDetail>
          <SponserWrapper>
            {!sponserName ? (
              <Sponser $isPlaceholder />
            ) : (
              <Sponser>
                <SponserName
                  data-element_id={ButtonId.Shorts.ButtonShortAdsClick}
                  data-tracking_payload={trackingPayload}
                  href={adLink}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {`${sponserName ? sponserName : '-'}`}
                </SponserName>
              </Sponser>
            )}
            <AdBadge>
              {t('short_advertisement', { ns: TranslationNamespace.POST })}
            </AdBadge>
          </SponserWrapper>
          <Caption
            $isPlaceholder={!adData}
            className="swiper-no-swiping"
            shouldShowAllCaption={shouldShowAllCaption}
            data-element_id={
              shouldShowAllCaption
                ? ButtonId.Shorts.ShortsCaptionClose
                : ButtonId.Shorts.ShortsCaptionOpen
            }
            data-tracking_payload={trackingPayload}
            onClick={e => {
              e.stopPropagation();
              return setShouldShowAllCaption(!shouldShowAllCaption);
            }}
          >
            {shouldShowAllCaption ? (
              <LinkSubstring>{caption}</LinkSubstring>
            ) : (
              caption
            )}
          </Caption>
          <CtaLink
            href={adLink}
            target="_blank"
            rel="noopener noreferrer"
            data-element_id={ButtonId.Shorts.ButtonShortAdsClick}
            data-tracking_payload={trackingPayload}
          >
            {ctaText}
          </CtaLink>
        </MessageDetail>

        <MessageMetadata>
          <ShareButtonWrapper>
            <ShareButton
              id={adId}
              shareType={ShareType.SHORT}
              shareTitleI18nKey={'title_share_shorts_link'}
              shareTitleI18nOption={{
                ns: TranslationNamespace.POST,
              }}
              username={sponserName}
              copyLinkBoxPosition={'left'}
              tooltipPosition={'left'}
            />
          </ShareButtonWrapper>
          <Tooltip
            position="left"
            renderHint={() => (
              <TooltipText>{isMuted ? 'Off' : 'On'}</TooltipText>
            )}
            trigger="hover"
          >
            <MuteButton
              aria-label={isMuted ? 'unmute this ad' : 'mute this ad'}
              data-element_id={
                isMuted ? ButtonId.All.ButtonMuted : ButtonId.All.ButtonVolume
              }
              data-tracking_payload={{
                isMuted,
                ...trackingPayload,
              }}
              onClick={() => {
                const videoElement = getVideoElement();
                if (!videoElement) return;
                setPlayerData({ isMuted: !isMuted });
              }}
            >
              <img
                src={isMuted ? MuteIconSource : AudioIconSource}
                width="32"
                height="32"
                alt={isMuted ? 'audio off' : 'audio on'}
              />
            </MuteButton>
          </Tooltip>
        </MessageMetadata>
      </ActionWrapper>
    </StyledShortAd>
  );
};

ShortAd.propTypes = {
  adLink: PropTypes.string.isRequired,
  adId: PropTypes.string.isRequired,
  currentLanguage: PropTypes.string,
  isActive: PropTypes.bool,
  isMuted: PropTypes.bool,
  adData: PropTypes.object,
  fetchOembedData: PropTypes.func,
  setPlayerData: PropTypes.func,
};

const StyledShortAd = styled.div`
  position: relative;
  margin: 0 auto;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  overflow: hidden;
  aspect-ratio: 9 / 16;
  @supports not (aspect-ratio: 9 / 16) {
    padding-top: 178%;
    height: 0;
  }
  ${media.mobile`
    width: auto;
    height: 100%;
    aspect-ratio: auto;
    padding-top: 0; 
  `};

  ::after {
    position: absolute;
    top: 0px;
    bottom: 0px;
    left: 0px;
    right: 0px;

    ${({ shouldShowAllCaption }) =>
      shouldShowAllCaption
        ? css`
            background: ${color.neutral['0_alpha_60']};
          `
        : css`
            background: linear-gradient(
              180deg,
              ${color.neutral['0_alpha_30']} 0%,
              ${color.neutral['0_alpha_5']} 16.68%,
              ${color.neutral['0_alpha_30']} 52.99%,
              ${color.neutral['0_alpha_60']} 100%
            );
          `};
    content: '';

    @media (min-width: ${breakpoint.mobile}px) {
      border-radius: 8px;
    }
  }
`;

const FadeOut = keyframes`
  from  {
    opacity: 1;
    transform: scale(1);
  }
  to  {
    opacity: 0;
    transform: scale(2);
    display: none;
  }
`;

const PlayerControlButton = styled.button`
  ${ResetButtonStyle};
  position: absolute;
  top: 50%;
  left: 50%;
  z-index: ${shortsPlayerZIndex.playerControl};
  transform: translate(-50%, -50%);
  > img {
    border-radius: 50%;
    padding: 6px;
    width: 72px;
    height: 72px;
    object-fit: contain;
    background-color: ${color.neutral['0_alpha_60']};
    ${({ isPlaying }) => {
      if (isPlaying)
        return css`
          animation: ${TRANSITION_MILLI_SECONDS}ms forwards ${FadeOut} ease-out;
        `;
    }}
  }
`;

const ActionWrapper = styled.div`
  position: absolute;
  bottom: 0px;
  left: 0px;
  right: 0px;
  z-index: ${shortsPlayerZIndex.actionWrapper};
  // padding right + gap + metadata
  padding: 0px ${16 + METADATA_DETAIL_GAP + METADATA_SIZE}px 20px 16px;
`;

const MessageMetadata = styled.div`
  position: absolute;
  bottom: 20px;
  right: 16px;
  display: flex;
  flex-direction: column;
  align-items: center;
  > div {
    width: ${METADATA_SIZE}px;
  }
`;

const MetadataStyle = css`
  margin-bottom: 16px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-end;
  font-size: 12px;
  font-weight: 600;
  color: ${color.neutral[100]};
  text-align: center;
  :last-child {
    margin-bottom: 0px;
  }
  > img {
    padding: 4px;
    width: ${METADATA_SIZE}px;
    height: ${METADATA_SIZE}px;
    object-fit: contain;
    @media (hover: hover) {
      :hover {
        border-radius: 50%;
        background-color: ${color.neutral['55_alpha_20']};
      }
    }
  }
`;

const ShareButtonWrapper = styled.div`
  ${MetadataStyle};
`;

const MuteButton = styled.button`
  ${ResetButtonStyle};
  ${MetadataStyle};
  margin-bottom: 0;
`;

const TooltipText = styled.div`
  height: 30px;
  margin-left: -28px;
  padding: 8px;
  border-radius: 8px;
  box-shadow: 0 2px 8px 0 ${color.neutral['0_alpha_50']};
  background-color: ${color.neutral['100_alpha_80']};
  color: ${color.black};
  text-align: center;
  white-space: nowrap;
  font-size: 14px;
  transform: translateY(8px);
`;

const Photo = styled.img`
  width: 100%;
  height: 100%;
  object-fit: contain;
`;

const VideoWrapper = styled.div`
  width: 100%;
  height: 100%;
  @supports not (aspect-ratio: 9 / 16) {
    position: absolute;
    top: 0;
    right: 0;
    left: 0;
    bottom: 0;
  }

  ${media.mobile`
    position: static;
  `}
  > video {
    width: 100%;
    height: 100%;
    object-fit: contain;
    border-radius: 8px;
  }
`;

const MessageDetail = styled.div`
  overflow: hidden;
  text-align: left;
  transition: transform 0.4s;
`;

const SponserWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
`;

const Sponser = styled.div`
  width: ${({ $isPlaceholder }) => ($isPlaceholder ? '30%' : 'auto')};
  min-height: ${({ $isPlaceholder }) => ($isPlaceholder ? '22px' : 'auto')};
  display: flex;
  align-items: center;
  background-color: ${({ $isPlaceholder }) =>
    $isPlaceholder ? color.neutral[10] : 'transparent'};
`;

const SponserName = styled.a`
  ${TextEllipsis};
  font-size: 16px;
  color: ${color.neutral[100]};
  line-height: 1.4;
  display: block;
`;

const AdBadge = styled.div`
  padding: 0px 4px;
  border-radius: 2px;
  background-color: ${color.neutral['20_alpha_80']};
  color: white;
  text-align: center;
  font-size: 12px;
  font-weight: 600;
  line-height: 150%; /* 18px */
  letter-spacing: 0.012px;
`;

const Caption = styled.div.attrs(({ $isPlaceholder }) => ({
  style: {
    backgroundColor: $isPlaceholder ? color.neutral[10] : 'transparent',
    minHeight: $isPlaceholder ? '20px' : 'auto',
  },
}))`
  text-overflow: ellipsis;
  height: auto;
  margin-bottom: 8px;
  max-width: 100%;
  color: ${color.neutral[100]};
  font-size: 14px;
  line-height: 1.4;
  text-align: left;
  white-space: pre-wrap;
  cursor: pointer;
  &::-webkit-scrollbar {
    display: none;
  }
  ${({ shouldShowAllCaption }) => {
    if (shouldShowAllCaption) {
      return css`
        max-height: 40vh;
        display: block;
        overflow-y: scroll;
        overflow-x: hidden;
      `;
    }
    return css`
      display: -webkit-box;
      -webkit-line-clamp: 1;
      -webkit-box-orient: vertical;
      overflow: hidden;
    `;
  }}
`;

const CtaLink = styled.a`
  ${ResetButtonStyle};
  border-radius: 4px;
  padding: 10px 8px;
  width: 100%;
  height: ${CTA_HEIGHT}px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${color.neutral[0]};
  font-weight: 600;
  background-color: white;
  font-size: 20px;
  margin-top: 16px;
  ${media.tablet`
    height: ${CTA_MOBILE_HEIGHT}px;
    font-size: 18px;
    font-size: 14px;
    padding: 4px 8px;
  `};
`;

export default ShortAd;
